import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";
import { ExternalLink, RefreshCw, ArrowLeft } from "lucide-react";

const STATUS_STYLE = {
  paid:      { fg: "#5db368", bg: "rgba(94, 179, 104, 0.12)", border: "rgba(94, 179, 104, 0.4)" },
  unpaid:    { fg: "#d4a04a", bg: "rgba(212, 160, 74, 0.12)", border: "rgba(212, 160, 74, 0.4)" },
  initiated: { fg: "#8a7e6d", bg: "rgba(138, 126, 109, 0.12)", border: "rgba(138, 126, 109, 0.4)" },
  expired:   { fg: "#c46a5a", bg: "rgba(196, 106, 90, 0.12)", border: "rgba(196, 106, 90, 0.4)" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.initiated;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-mono-eav text-[10px] tracking-wider uppercase"
      style={{ color: s.fg, background: s.bg, border: `1px solid ${s.border}` }}
      data-testid={`payment-status-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.fg }} />
      {status || "—"}
    </span>
  );
}

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ all: 0, paid: 0, unpaid: 0, initiated: 0, expired: 0 });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments", {
        params: filter ? { status: filter, limit: 200 } : { limit: 200 },
      });
      setRows(data.items || []);
      setTotals(data.totals || totals);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const paidAmount = rows
    .filter((r) => r.payment_status === "paid")
    .reduce((acc, r) => acc + (r.amount || 0), 0);
  const pipelineAmount = rows
    .filter((r) => r.payment_status !== "paid")
    .reduce((acc, r) => acc + (r.amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="admin-payments-page">
      <Helmet><title>Admin · Payments</title></Helmet>
      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#b8963e] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Admin dashboard
        </Link>

        <div className="flex items-center justify-between mt-4 mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-serif-display text-3xl text-[#f4efe6]">Payments</h1>
            <p className="text-sm text-[#8a7e6d] mt-1">
              Stripe Checkout transactions across all audits — for reconciling refunds, disputes, and stuck webhooks.
            </p>
          </div>
          <button onClick={load}
                  data-testid="admin-payments-refresh"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[#2a2620] text-[#c9bca4] hover:border-[#b8963e] hover:text-[#b8963e] rounded-sm text-sm font-mono-eav tracking-wider uppercase">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Paid (visible)"    value={`$${paidAmount.toFixed(0)}`} sub={`${totals.paid} session${totals.paid === 1 ? "" : "s"}`} accent="#5db368" />
          <StatCard label="Pipeline (visible)" value={`$${pipelineAmount.toFixed(0)}`} sub="Initiated or unpaid" accent="#d4a04a" />
          <StatCard label="Total sessions"     value={totals.all} sub="All-time on this platform" />
          <StatCard label="Expired"            value={totals.expired ?? 0} sub="Customer abandoned" accent="#c46a5a" />
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-payments-filters">
          <FilterChip current={filter} value="" onClick={setFilter}>All ({totals.all})</FilterChip>
          <FilterChip current={filter} value="paid" onClick={setFilter}>Paid ({totals.paid})</FilterChip>
          <FilterChip current={filter} value="unpaid" onClick={setFilter}>Unpaid ({totals.unpaid})</FilterChip>
          <FilterChip current={filter} value="initiated" onClick={setFilter}>Initiated ({totals.initiated})</FilterChip>
          <FilterChip current={filter} value="expired" onClick={setFilter}>Expired ({totals.expired})</FilterChip>
        </div>

        {loading ? (
          <p className="text-[#8a7e6d]">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="border border-[#2a2620] rounded-sm p-14 text-center">
            <p className="font-mono-eav text-sm text-[#8a7e6d]">No payments to show.</p>
          </div>
        ) : (
          <div className="border border-[#2a2620] rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0c0a08] border-b border-[#2a2620]">
                <tr className="font-mono-eav text-[10px] tracking-[0.18em] uppercase text-[#8a7e6d]">
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Company</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Package</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Audit</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Created</th>
                  <th className="text-right px-4 py-3">Stripe</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.session_id} className="border-b border-[#2a2620] hover:bg-[#0c0a08]/60"
                      data-testid={`payment-row-${r.session_id}`}>
                    <td className="px-4 py-3">
                      <div className="text-[#f4efe6]">{r.customer_email || "—"}</div>
                      <div className="text-[10px] font-mono-eav text-[#5e5448] mt-0.5 truncate max-w-[160px]" title={r.session_id}>
                        {r.session_id?.slice(0, 18)}…
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-[#c9bca4]">{r.company_name || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[#c9bca4]">{r.package_label || r.package_id}</td>
                    <td className="px-4 py-3 text-right font-mono-eav text-[#f4efe6]">
                      ${r.amount?.toFixed?.(2) ?? r.amount}
                      <span className="ml-1 text-[10px] text-[#5e5448] uppercase">{r.currency}</span>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={r.payment_status} /></td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {r.audit_id && (
                        <Link to={`/snapshot/${r.audit_id}`}
                              data-testid={`payment-audit-link-${r.session_id}`}
                              className="text-[#b8963e] hover:underline text-xs font-mono-eav">
                          view audit
                        </Link>
                      )}
                      {r.audit_status && (
                        <div className="text-[10px] text-[#5e5448] mt-0.5">{r.audit_status}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[#8a7e6d] text-xs">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.stripe_url && (
                        <a href={r.stripe_url} target="_blank" rel="noreferrer"
                           data-testid={`payment-stripe-link-${r.session_id}`}
                           title="Open in Stripe Dashboard"
                           className="inline-flex items-center gap-1 text-[#b8963e] hover:underline">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, sub, accent = "#b8963e" }) {
  return (
    <div className="eav-card p-4" data-testid={`payment-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <p className="text-[10px] font-mono-eav tracking-[0.18em] uppercase font-bold" style={{ color: accent }}>{label}</p>
      <p className="mt-2 font-serif-display font-bold text-3xl text-[#f4efe6] leading-none">{value ?? "—"}</p>
      {sub && <p className="mt-2 text-[11px] text-[#8a7e6d] leading-snug">{sub}</p>}
    </div>
  );
}

function FilterChip({ current, value, onClick, children }) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      data-testid={`payments-filter-${value || "all"}`}
      className={`px-3 py-1 text-xs rounded-sm font-mono-eav tracking-wider uppercase
        ${active ? "bg-[#b8963e] text-[#0c0a08]" : "text-[#c9bca4] border border-[#2a2620] hover:border-[#b8963e]"}`}>
      {children}
    </button>
  );
}
